// pages/W_HR_R430.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataGrid, { Column, Paging, FilterRow, Sorting } from "devextreme-react/data-grid";
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import { Button } from 'devextreme-react/button';
import 'devextreme/dist/css/dx.material.blue.light.css';
import API_URL from '../config';

export default function W_HR_R430({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [masterData, setMasterData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [comboLoading, setComboLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [error, setError] = useState("");

  const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear().toString();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${y}${m}${d}`;
  };

  useEffect(() => {
    (async () => {
      setComboLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/oracle/PT_cust`);
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setCustList(res.data.data || []);
      } catch (e) {
        setError(`CUST 로드 오류: ${e.message}`);
      } finally {
        setComboLoading(false);
      }
    })();
  }, []);
  
  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      setSelectedCust(found?.CODE_CD || null);
      if(!found) setError(`사용자 코드(${user.username})가 목록에 없습니다.`);
    }
  }, [user, custList]);
  
  useEffect(() => {
    setDeviceList([]);
    setSelectedDevice(null);
    if (!selectedCust) return;

    (async () => {
      setDeviceLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/oracle/PT_device`, { params: { custCd: selectedCust } });
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setDeviceList(res.data.data || []);
      } catch (e) {
        setError(`DEVICE 로드 오류: ${e.message}`);
      } finally {
        setDeviceLoading(false);
      }
    })();
  }, [selectedCust]);

  const fetchMaster = async () => {
    if (!selectedCust) {
      setError("먼저 CUST를 선택하세요.");
      return;
    }
    setLoading(true);
    setMasterData([]);
    setDetailData([]);
    setError("");
    try {
      const params = {
        inCustCd: selectedCust,
        inDevice: selectedDevice || 'ALL',
        inLotNo: '%',
        inLotSub: '%',
        inDateFrom: formatDate(dateFrom),
        inDateTo: formatDate(dateTo)
      };
      const res = await axios.get(`${API_URL}/api/oracle/W_HR_R430/master`, { params });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
      setMasterData(res.data.data || []);
    } catch (e) {
      const errorMessage = e.response?.data?.retMsg || e.message;
      setError(`MASTER 조회 오류: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const onMasterRowClick = (e) => {
    if (!e.data) return;
    const { LOT_NO, SUB } = e.data;
    fetchDetail(LOT_NO, SUB);
  }

  const fetchDetail = async (lotNo, lotSub) => {
    setDetailLoading(true);
    setDetailData([]);
    setError("");
    try {
      const params = { inLotNo: lotNo, inLotSub: lotSub };
      const res = await axios.get(`${API_URL}/api/oracle/W_HR_R430/detail`, { params });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
      setDetailData(res.data.data || []);
    } catch (e) {
      const errorMessage = e.response?.data?.retMsg || e.message;
      setError(`DETAIL 조회 오류: ${errorMessage}`);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    // [수정] 페이지 전체를 Flexbox 레이아웃으로 설정
    <div style={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3>LOT SHIP & HISTORY</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 15, alignItems: "center" }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" />
          <DateBox  value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" />
          <SelectBox  dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedCust(e.value)} placeholder="CUST" label="CUST" searchEnabled showClearButton disabled={comboLoading || (user?.role === 'CUST')} />
          <SelectBox  dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} placeholder="DEVICE (전체)" label="DEVICE" searchEnabled showClearButton disabled={deviceLoading} />
          <Button text="Search" type="default" icon="search" onClick={fetchMaster} disabled={loading} />
        </div>
        {error && <p style={{ color: "red", fontWeight: 'bold' }}>{error}</p>}
      </div>

      {/* 2. 그리드 영역 (남은 공간을 모두 차지하며, 가로로 배치) */}
      <div style={{ flex: 1, display: 'flex', gap: 20, overflow: 'hidden' }}>
        {/* Master 그리드 컨테이너 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <h4>Master - Ship List</h4>
          {loading && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading...</div>
            </div>
          )}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <DataGrid dataSource={masterData} keyExpr={['LOT_NO', 'SUB']} height="100%" showBorders columnAutoWidth onRowClick={onMasterRowClick} hoverStateEnabled>
              <Paging defaultPageSize={10} />
              <FilterRow visible />
              <Sorting mode="multiple" />           
              <Column dataField="LOT_NO" caption="LOT NO" />
              <Column dataField="SUB" caption="SUB" />
              <Column dataField="SHIP_DATE" caption="SHIP DATE" />
            </DataGrid>
          </div>
        </div>

        {/* Detail 그리드 컨테이너 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <h4>Detail - Lot History</h4>
          {detailLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading...</div>
            </div>
          )}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <DataGrid dataSource={detailData} keyExpr="TRNX_SEQ" height="100%" showBorders columnAutoWidth>
              <Paging defaultPageSize={10} />
              <FilterRow visible />
              <Sorting mode="multiple" />          
              <Column dataField="LOT_NO" caption="LOT NO" />
              <Column dataField="LOT_NO_SUB" caption="SUB" />
              <Column dataField="TRNX_SEQ" caption="SEQ" />
              <Column dataField="TRNX_CD" caption="TRNX CD" />
              <Column dataField="TRNX_TM" caption="TRNX TIME"/>
              <Column dataField="TRNX_QTY_FROM" caption="QTY FROM" />
              <Column dataField="TRNX_QTY_TO" caption="QTY TO" />
              <Column dataField="QTY_DIFF" caption="QTY DIFF" />
              <Column dataField="TRNX_OPER_FROM" caption="OPER FROM" />
              <Column dataField="TRNX_OPER_TO" caption="OPER TO" />
              <Column dataField="COMM" caption="COMMENT" width={200}/>
            </DataGrid>
          </div>
        </div>
      </div>
    </div>
  );
}